import { useState } from 'react'

import { RichText } from '~/components/RichText'
import { AuthorCard } from '~/components/Author'
import { TableOfContent } from '../TableOfContent'
import { getTocItems } from '../TableOfContent/utils'
import { Share, ShareRow } from '../Share'
import { ArticleAsidePreview } from './previews/ArticleAsidePreview'
import { ArticleHero } from './components/ArticleHero'

import { StructuredSchema } from '~/components/StructuredSchema'
import { useRootContext } from '~/hooks/useRootContext'
import { FlexContainer, FlexItem } from '~/components/Grid'
import classNames from 'classnames'
import { SlideAside } from '../SlideAside'
import { TypeArticle, TypeAuthorSkeleton } from '~/types/contentful'
import { Asset, Entry } from 'contentful'

type ArticleProps = {
  data: TypeArticle<'WITHOUT_UNRESOLVABLE_LINKS', string>
}

export const Article = ({ data }: ArticleProps) => {
  const { body, author, title, abstract, category, illustration } = data.fields

  const { url } = useRootContext()

  const [richTextArticle, setRichTextArticle] = useState<HTMLElement | null>(null)
  const toc = getTocItems(body)

  const illustrationImage = illustration as Asset<'WITHOUT_UNRESOLVABLE_LINKS', string>
  const authorEntry = author as Entry<TypeAuthorSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>

  return (
    <div>
      <ArticleHero data={data} />
      <div className="relative py-0 max-xl:px-0 px-25 max-xl:flex max-xl:flex-wrap max-xl:justify-between">
        <div className="absolute left-0 h-full z-1 max-xl:static max-xl:h-auto max-xl:mb-7 max-md:mb-5 max-md:-left-3">
          <div className="max-xl:static sticky left-0 top-[calc(32px_+_var(--header-height))]">
            {toc && <TableOfContent items={toc} richTextArticle={richTextArticle} />}
          </div>
        </div>
        <div className="absolute right-0 h-full z-1 max-xl:static max-xl:h-auto max-xl:mb-7 max-md:mb-5 max-md:mr-[-10px]">
          <div className="max-xl:static sticky left-0 top-[calc(32px_+_var(--header-height))]">
            <Share articleTitle={title} articleAbstract={abstract} />
          </div>
        </div>

        <FlexContainer
          justify="between"
          className="max-2xl:!transform-none [&:not(:last-child]:mb-25 max-xl:[&:not(:last-child]:mb-15 max-md:[&:not(:last-child]:mb-6"
        >
          <FlexItem
            className={classNames('relative max-lg:mb-10')}
            desktop={8}
            laptop={8}
            tablet={9}
          >
            {abstract && (
              <p className="whitespace-pre-wrap mb-15 max-xl:mb-10 max-md:mb-7 text-m">
                {abstract}
              </p>
            )}
            {body && <RichText doc={body} ref={setRichTextArticle} />}
          </FlexItem>
          <SlideAside className="hidden lg:block max-lg:w-full w-[30%]" key={data.sys.id}>
            {category && <ArticleAsidePreview data={data} />}
          </SlideAside>
        </FlexContainer>
        <FlexContainer
          justify="between"
          className="max-2xl:!transform-none [&:not(:last-child]:mb-25 max-xl:[&:not(:last-child]:mb-15 max-md:[&:not(:last-child]:mb-6 lg:gap-15 lg:mt-15"
        >
          {authorEntry && <AuthorCard author={authorEntry} />}
          <ShareRow articleTitle={title} articleAbstract={abstract} articleId={data.sys.id} />
        </FlexContainer>
      </div>
      <StructuredSchema
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          url: url.href,
          headline: title,
          image: {
            '@type': 'ImageObject',
            url: `https:${illustrationImage.fields?.file?.url}`,
          },
          description: abstract,
          datePublished: data.sys.createdAt,
          dateModified: data.sys.updatedAt,
          author: {
            '@type': 'Person',
            name: authorEntry.fields.name,
            url: `${url.origin}/author/${authorEntry.fields.slug}`,
            description: authorEntry.fields.bio,
            image: {
              '@type': 'ImageObject',
              url: authorEntry.fields.avatar
                ? `https:${(authorEntry.fields.avatar as Asset<'WITHOUT_UNRESOLVABLE_LINKS', string>)?.fields?.file?.url}`
                : `${url.origin}/placeholder/user.png`,
            },
            sameAs: [authorEntry.fields.linkedin],
            worksFor: {
              '@type': 'Organization',
              name: 'career.io',
              url: 'https://career.io',
            },
          },
          publisher: {
            '@type': 'Organization',
            name: 'career.io',
            url: 'https://career.io',
            logo: {
              '@type': 'ImageObject',
              url: `${url.origin}/images/cio-logo.jpg`,
            },
            sameAs: [
              'https://www.linkedin.com/company/career-io',
              'https://www.facebook.com/WeAreCareer.io',
              'https://www.instagram.com/wearecareerio',
              'https://www.youtube.com/@careerio',
              'https://www.tiktok.com/@career.io',
            ],
          },
        }}
      />
    </div>
  )
}
